import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { FileUp } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { useQuoteBuilderApi } from '../api/client'
import { applyQuoteChange, invalidateQuoteOverview } from '../api/queries'
import { importAqproj, importErrorMessage } from './api'

/** "Import AQProj": an AutoQuotes project file becomes a new quote, then opens its lines in the import
 * review, already approved, while SmartQuote finishes what the catalog match left open (USA-1340). */
export function AqprojImportAction() {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const upload = useMutation({
    mutationFn: (file: File) => importAqproj(api, file),
    onSuccess: ({ quote, document_import: item }) => {
      applyQuoteChange(client, quote.id, quote)
      void invalidateQuoteOverview(client)
      void navigate({
        to: '/quotes/$quoteId/workflow/$stage',
        params: { quoteId: quote.id, stage: 'equipment' },
        search: { import: 1, documentImport: item.id },
      })
    },
    onError: (error) => toast.error(importErrorMessage(error, 'The AutoQuotes project could not be imported. Try again.')),
  })
  return (
    <>
      <input
        accept=".aqproj"
        aria-label="AutoQuotes project file"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) upload.mutate(file)
        }}
        ref={input}
        type="file"
      />
      <LoadingButton loading={upload.isPending} loadingText="Importing…" onClick={() => input.current?.click()} variant="outline">
        <FileUp aria-hidden="true" />
        Import AQProj
      </LoadingButton>
    </>
  )
}
